import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import type { MOLARITY_CALCULATOR_STRINGS } from "@/components/tools/molarity-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { formulaToUnicode } from "@/lib/tool-kit/chemistry/formula";
import type {
  MolaritySolve,
  MolarityValues,
} from "@/lib/tool-kit/chemistry/solutions";
import type { Substance } from "@/lib/tool-kit/chemistry/substance";
import {
  findUnit,
  fromBase,
  MASS,
  MOLARITY,
  toBase,
  VOLUME,
} from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

type Strings = typeof MOLARITY_CALCULATOR_STRINGS;

const MOLAR_MASS_ANSWER_UNIT = "g/mol";

export interface Fields {
  massText: string;
  massUnit: string;
  volumeText: string;
  volumeUnit: string;
  molarityText: string;
  molarityUnit: string;
}

function conversionLines(
  strings: Strings,
  fmt: NumberFormatter,
  fields: Fields,
  solve: MolaritySolve
): string[] {
  const rows = [
    {
      skip: solve === "mass",
      text: fields.massText,
      unit: fields.massUnit,
      family: MASS,
      baseId: "g",
      baseUnit: "g",
    },
    {
      skip: solve === "volume",
      text: fields.volumeText,
      unit: fields.volumeUnit,
      family: VOLUME,
      baseId: "L",
      baseUnit: "L",
    },
    {
      skip: solve === "molarity",
      text: fields.molarityText,
      unit: fields.molarityUnit,
      family: MOLARITY,
      baseId: "M",
      baseUnit: strings.unitMolar,
    },
  ];
  const lines: string[] = [];
  for (const row of rows) {
    const typed = parseMeasuredNumber(row.text);
    if (row.skip || typed === null || row.unit === row.baseId) {
      continue;
    }
    lines.push(
      t(strings.convertLine, {
        value: formatPlain(fmt, typed),
        unit: findUnit(row.family, row.unit).symbol,
        base: formatPlain(fmt, toBase(typed, row.family, row.unit)),
        baseUnit: row.baseUnit,
      })
    );
  }
  return lines;
}

function workingLines(
  strings: Strings,
  fmt: NumberFormatter,
  solve: MolaritySolve,
  v: MolarityValues,
  sig: number
): string[] {
  const g = sig + 1;
  const p = (value: number) => formatPlain(fmt, value);
  const s = (value: number) => formatSig(fmt, value, g);
  const f = (value: number) => formatSig(fmt, value, sig);
  const common = {
    molar: p(v.molarMass),
    moles: s(v.moles),
  };
  if (solve === "molarity") {
    return [
      t(strings.molesFromMass, { ...common, mass: p(v.massG) }),
      t(strings.concLine, {
        ...common,
        volume: p(v.volumeL),
        conc: f(v.molarity),
      }),
    ];
  }
  if (solve === "mass") {
    return [
      t(strings.molesFromConc, {
        ...common,
        conc: p(v.molarity),
        volume: p(v.volumeL),
      }),
      t(strings.massLine, { ...common, mass: f(v.massG) }),
    ];
  }
  if (solve === "volume") {
    return [
      t(strings.molesFromMass, { ...common, mass: p(v.massG) }),
      t(strings.volumeLine, {
        ...common,
        conc: p(v.molarity),
        volume: f(v.volumeL),
      }),
    ];
  }
  return [
    t(strings.molesFromConc, {
      ...common,
      conc: p(v.molarity),
      volume: p(v.volumeL),
    }),
    t(strings.molarLine, {
      moles: s(v.moles),
      mass: p(v.massG),
      molar: f(v.molarMass),
    }),
  ];
}

function Extras({
  v,
  eq,
  solvent,
  sig,
  strings,
  fmt,
}: {
  v: MolarityValues;
  eq: number | null;
  solvent: number | null;
  sig: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  return (
    <>
      {eq === null ? null : (
        <StatCard
          label={strings.normality}
          value={`${formatSig(fmt, v.molarity * eq, sig)} eq/L`}
        />
      )}
      {solvent === null ? null : (
        <StatCard
          label={strings.molality}
          value={`${formatSig(fmt, v.moles / solvent, sig)} mol/kg`}
        />
      )}
    </>
  );
}

export function MolarityAnswer({
  solve,
  v,
  fields,
  substance,
  sig,
  eq,
  solvent,
  strings,
}: {
  strings: Strings;
  solve: MolaritySolve;
  v: MolarityValues;
  fields: Fields;
  substance: Substance;
  sig: number;
  eq: number | null;
  solvent: number | null;
}) {
  const fmt = useToolNumber();
  const mass = formatSig(fmt, fromBase(v.massG, MASS, fields.massUnit), sig);
  const volume = formatSig(
    fmt,
    fromBase(v.volumeL, VOLUME, fields.volumeUnit),
    sig
  );
  const molarity = formatSig(
    fmt,
    fromBase(v.molarity, MOLARITY, fields.molarityUnit),
    sig
  );
  const molar = formatSig(fmt, v.molarMass, sig);
  const massSymbol = findUnit(MASS, fields.massUnit).symbol;
  const volumeSymbol = findUnit(VOLUME, fields.volumeUnit).symbol;
  const molaritySymbol = findUnit(MOLARITY, fields.molarityUnit).symbol;
  const answers = {
    molarity: {
      label: strings.answerMolarity,
      value: molarity,
      unit: molaritySymbol,
    },
    mass: { label: strings.answerMass, value: mass, unit: massSymbol },
    volume: { label: strings.answerVolume, value: volume, unit: volumeSymbol },
    molarMass: {
      label: strings.answerMolarMass,
      value: molar,
      unit: MOLAR_MASS_ANSWER_UNIT,
    },
  }[solve];
  const solute =
    substance.kind === "ok" && substance.formula
      ? formulaToUnicode(substance.formula)
      : strings.soluteGeneric;
  const lines = [
    ...conversionLines(strings, fmt, fields, solve),
    ...workingLines(strings, fmt, solve, v, sig),
  ];
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={answers.label}
          value={`${answers.value} ${answers.unit}`}
        />
        <StatCard
          label={strings.moles}
          value={`${formatSig(fmt, v.moles, sig)} mol`}
        />
        <Extras
          eq={eq}
          fmt={fmt}
          sig={sig}
          solvent={solvent}
          strings={strings}
          v={v}
        />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        {eq === null ? null : (
          <p>
            {t(strings.normalityLine, {
              conc: formatPlain(fmt, v.molarity),
              eq: formatPlain(fmt, eq),
              normality: formatSig(fmt, v.molarity * eq, sig),
            })}
          </p>
        )}
        {solvent === null ? null : (
          <p>
            {t(strings.molalityLine, {
              moles: formatSig(fmt, v.moles, sig + 1),
              solvent: formatPlain(fmt, solvent),
              molality: formatSig(fmt, v.moles / solvent, sig),
            })}
          </p>
        )}
      </div>
      {solve === "mass" || solve === "molarity" || solve === "volume" ? (
        <div className="space-y-2">
          <h3 className="font-semibold text-foreground text-lg">
            {strings.recipeHeading}
          </h3>
          <p className="text-foreground text-sm leading-relaxed">
            {t(strings.recipe, {
              mass,
              massUnit: massSymbol,
              solute,
              volume,
              volumeUnit: volumeSymbol,
              molarity,
              molarityUnit: molaritySymbol,
            })}
          </p>
        </div>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, answers)}
      />
    </>
  );
}
